import React, { useRef } from 'react';
import { Tag, Tooltip } from 'antd';
import { LinkOutlined, RightOutlined, TeamOutlined } from '@ant-design/icons';
import { useNotify } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { getTagForServiceState } from '@src/components/Common/Tags';
import ServiceTabs from './ServiceTabs';
import CollapsePanel from '@src/components/Common/CollapsePanel';
import { useExpandedRow } from '@src/components/Common/OpenCard';
import { ServiceCatalogueConfig } from './types';

/** the tags a service carries, capped so one heavily-tagged service cannot own the row */
const TAG_LIMIT = 4;

/**
 * Copies the link that opens this service's card — the page's own route with
 * the service named in the hash, which is what useHashAccordion reads. The
 * page's filters are left off: the link is for someone else, and a filter that
 * suits the sender could hide the very service it points at.
 */
const CopyLink = ({ name }: { name: string }) => {
    const notify = useNotify();
    const { pathname } = useLocation();

    const copy = (event: React.SyntheticEvent) => {
        // the head opens the card; copying its link should not
        event.stopPropagation();
        const url = `${window.location.origin}${window.location.pathname}#${pathname}#${encodeURIComponent(name)}`;
        navigator.clipboard?.writeText(url)
            .then(() => notify('Link copied to clipboard'))
            .catch(() => notify('Could not copy', 'error'));
    };

    return (
        <Tooltip title="Copy link">
            <button
                type="button"
                className="svc-card__link"
                aria-label={`Copy link to ${name}`}
                onClick={copy}
                // Enter and Space on the button are its own, not the head's
                onKeyDown={(event) => event.stopPropagation()}
            >
                <LinkOutlined />
            </button>
        </Tooltip>
    );
};

/**
 * One service in the catalogue: a card that says what the service is, and
 * opens in place onto everything about it. The card is the control — there is
 * no separate "view" affordance, because opening it is the only thing it does.
 */
const ServiceCard = ({
    record,
    config,
    open,
    toggle,
    scrollIntoView,
    dependant,
}: {
    record: any;
    config: ServiceCatalogueConfig;
    open: boolean;
    toggle: () => void;
    /** a deep link asked for this card; bring it into view once it is rendered */
    scrollIntoView?: boolean;
    dependant?: boolean;
}) => {
    const ref = useRef<HTMLElement>(null);
    const headRef = useRef<HTMLDivElement>(null);

    // opened — by a click or by a link — it follows the rules every row that
    // opens in place does (see useExpandedRow), with half of each card either
    // side in view: a whole one is too tall to give the room to
    useExpandedRow(ref, headRef, open, { deepLinked: scrollIntoView, neighbourShare: 0.5 });

    const description = record.schema?.schema?.description;
    const tags: string[] = record.tags || [];

    return (
        <article
            ref={ref}
            className={`svc-card${open ? ' svc-card--open' : ''}`}
        >
            <div
                ref={headRef}
                className="svc-card__head"
                role="button"
                tabIndex={0}
                aria-expanded={open}
                aria-label={`${open ? 'Collapse' : 'Expand'} ${record.name}`}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                <RightOutlined className="svc-card__chev" aria-hidden="true" />

                <div className="svc-card__body">
                    <div className="svc-card__title">
                        <h3 className="svc-card__name">{record.name}</h3>
                        <CopyLink name={record.name} />
                        {!dependant && getTagForServiceState(record.state)}
                        {tags.slice(0, TAG_LIMIT).map((tag) => (
                            <Tag key={tag} className="svc-tag">
                                #{tag}
                            </Tag>
                        ))}
                        {tags.length > TAG_LIMIT && (
                            <Tooltip title={tags.slice(TAG_LIMIT).map((tag) => `#${tag}`).join('  ')}>
                                <Tag className="svc-tag">+{tags.length - TAG_LIMIT}</Tag>
                            </Tooltip>
                        )}
                    </div>

                    {/* what the service is, for choosing between closed cards —
                        the open one is showing the whole of it already */}
                    {description && !open && <p className="svc-card__desc">{description}</p>}
                </div>

                {record.owner?.name && (
                    <span className="svc-card__owner">
                        <TeamOutlined className="svc-card__owner-icon" aria-label="Owner" />
                        <Tooltip placement="topRight" title={record.owner.name}>
                            <span className="svc-card__owner-name">{record.owner.name}</span>
                        </Tooltip>
                    </span>
                )}
            </div>

            {/* mounted only once open, so a page of cards costs one card's worth
                of requests rather than every tab of every service */}
            <CollapsePanel open={open} className="svc-card__panel">
                <ServiceTabs record={record} config={config} dependant={dependant} />
            </CollapsePanel>
        </article>
    );
};

export default ServiceCard;
